import React, { useState, useEffect, useCallback } from 'react';
import { PRESET_COLORS, colorValueToCss } from './colorUtils';

// ============================================================================
// localStorage Utilities
// ============================================================================

const RECENT_COLORS_KEY = 'colorPickerRecentColors';
const MAX_RECENT_COLORS = 10;

/**
 * Get recent colors from localStorage
 * @returns {Array} Array of color value objects
 */
export function getRecentColors() {
  try {
    const stored = localStorage.getItem(RECENT_COLORS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed.slice(0, MAX_RECENT_COLORS);
      }
    }
  } catch (e) {
    console.warn('Failed to load recent colors:', e);
  }
  return [];
}

/**
 * Add a color to recent colors in localStorage
 * @param {object} colorValue - Color value object to add
 */
export function addToRecentColors(colorValue) {
  if (!colorValue || typeof colorValue !== 'object') {
    return;
  }

  try {
    const recent = getRecentColors();

    // Create a string key for comparison
    const colorKey = JSON.stringify(colorValue);

    // Remove existing if present
    const filtered = recent.filter((c) => JSON.stringify(c) !== colorKey);

    // Add to front
    const updated = [colorValue, ...filtered].slice(0, MAX_RECENT_COLORS);

    localStorage.setItem(RECENT_COLORS_KEY, JSON.stringify(updated));
  } catch (e) {
    console.warn('Failed to save recent color:', e);
  }
}

// ============================================================================
// ColorSwatch Component
// ============================================================================

/**
 * Individual color swatch with checkered background for transparency
 */
function ColorSwatch({ colorValue, onClick, size = 'md', selected = false, title }) {
  const cssColor = colorValueToCss(colorValue);
  const isTransparent = colorValue?.alpha === 0 || cssColor === 'transparent';

  // Checkered pattern for transparency backgrounds
  const checkeredBg = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8'%3E%3Crect x='0' y='0' width='4' height='4' fill='%23ccc'/%3E%3Crect x='4' y='4' width='4' height='4' fill='%23ccc'/%3E%3Crect x='4' y='0' width='4' height='4' fill='%23fff'/%3E%3Crect x='0' y='4' width='4' height='4' fill='%23fff'/%3E%3C/svg%3E")`;

  const sizeClasses = {
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  return (
    <button
      type="button"
      onClick={() => onClick(colorValue)}
      className={`
        ${sizeClasses[size]}
        rounded
        border
        ${selected ? 'border-blue-500 ring-2 ring-blue-300' : 'border-gray-200 hover:border-gray-400'}
        overflow-hidden
        cursor-pointer
        transition-all
        flex-shrink-0
      `}
      style={{
        backgroundImage: isTransparent || (colorValue?.alpha !== undefined && colorValue.alpha < 100) ? checkeredBg : 'none',
        backgroundColor: '#fff',
      }}
      title={title || cssColor}
    >
      <div
        className="w-full h-full"
        style={{
          background: cssColor,
        }}
      />
    </button>
  );
}

// ============================================================================
// Section Header Component
// ============================================================================

function SectionHeader({ title, count }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</h4>
      {count !== undefined && (
        <span className="text-[10px] text-gray-400">{count}</span>
      )}
    </div>
  );
}

// ============================================================================
// Empty State Component
// ============================================================================

function EmptyState({ message }) {
  return (
    <div className="py-2 text-center">
      <p className="text-xs text-gray-400">{message}</p>
    </div>
  );
}

// ============================================================================
// ColorPresets Component
// ============================================================================

/**
 * ColorPresets - Display preset colors, recent colors, and saved page colors
 *
 * @param {Object} props
 * @param {Function} props.onSelectColor - Callback when user clicks a preset (colorValue: object) => void
 * @param {Object} props.currentColor - Current color value (for "save" functionality)
 * @param {Array} props.savedColors - Array of saved colors from page settings
 * @param {Function} props.onSaveColor - Callback to save current color to page (value: object, label: string) => void
 * @param {Function} props.onDeleteSavedColor - Callback to delete a saved color (id: string) => void
 */
export default function ColorPresets({
  onSelectColor,
  currentColor,
  savedColors = [],
  onSaveColor,
  onDeleteSavedColor,
}) {
  const [recentColors, setRecentColors] = useState([]);
  const [saveLabel, setSaveLabel] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Load recent colors on mount
  useEffect(() => {
    setRecentColors(getRecentColors());
  }, []);

  // Listen for storage changes from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === RECENT_COLORS_KEY) {
        setRecentColors(getRecentColors());
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Handle selecting a preset color (hex string from PRESET_COLORS)
  const handlePresetClick = useCallback(
    (hexColor) => {
      const colorValue = {
        type: 'solid',
        color: hexColor,
        alpha: 100,
      };
      onSelectColor(colorValue);
      addToRecentColors(colorValue);
      setRecentColors(getRecentColors());
    },
    [onSelectColor]
  );

  // Handle selecting a recent or saved color
  const handleColorValueClick = useCallback(
    (colorValue) => {
      onSelectColor(colorValue);
      addToRecentColors(colorValue);
      setRecentColors(getRecentColors());
    },
    [onSelectColor]
  );

  // Handle saving current color
  const handleSaveColor = useCallback(() => {
    if (onSaveColor && saveLabel.trim() && currentColor) {
      onSaveColor(currentColor, saveLabel.trim());
      setSaveLabel('');
      setIsSaving(false);
    }
  }, [onSaveColor, saveLabel, currentColor]);

  // Handle key press in save input
  const handleSaveKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        handleSaveColor();
      } else if (e.key === 'Escape') {
        setIsSaving(false);
        setSaveLabel('');
      }
    },
    [handleSaveColor]
  );

  return (
    <div className="space-y-4">
      {/* Default Colors Section */}
      <div>
        <SectionHeader title="Default Colors" />
        <div className="grid grid-cols-8 gap-1">
          {PRESET_COLORS.map((hexColor, index) => (
            <button
              key={`${hexColor}-${index}`}
              type="button"
              onClick={() => handlePresetClick(hexColor)}
              className="w-6 h-6 rounded border border-gray-200 hover:border-gray-400 cursor-pointer transition-colors"
              style={{ backgroundColor: hexColor }}
              title={hexColor}
            />
          ))}
        </div>
      </div>

      {/* Recent Colors Section */}
      <div>
        <SectionHeader title="Recent Colors" count={recentColors.length} />
        {recentColors.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {recentColors.map((colorValue, index) => (
              <ColorSwatch
                key={`recent-${index}`}
                colorValue={colorValue}
                onClick={handleColorValueClick}
                size="md"
              />
            ))}
          </div>
        ) : (
          <EmptyState message="No recent colors" />
        )}
      </div>

      {/* Saved Colors Section */}
      <div>
        <SectionHeader title="Saved Colors" count={savedColors.length} />
        {savedColors.length > 0 ? (
          <div className="space-y-1">
            {savedColors.map((savedColor) => (
              <div
                key={savedColor.id}
                className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50 group"
              >
                <ColorSwatch
                  colorValue={savedColor.value}
                  onClick={handleColorValueClick}
                  size="md"
                />
                <span
                  className="flex-1 text-xs text-gray-700 truncate cursor-pointer"
                  onClick={() => handleColorValueClick(savedColor.value)}
                  title={savedColor.label}
                >
                  {savedColor.label}
                </span>
                {onDeleteSavedColor && (
                  <button
                    type="button"
                    onClick={() => onDeleteSavedColor(savedColor.id)}
                    className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-opacity"
                    title="Delete saved color"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <EmptyState message="No saved colors" />
        )}

        {/* Save Current Color */}
        {onSaveColor && currentColor && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            {isSaving ? (
              <div className="flex items-center gap-2">
                <ColorSwatch colorValue={currentColor} onClick={() => {}} size="sm" />
                <input
                  type="text"
                  value={saveLabel}
                  onChange={(e) => setSaveLabel(e.target.value)}
                  onKeyDown={handleSaveKeyDown}
                  placeholder="Color name..."
                  className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded bg-gray-50 focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleSaveColor}
                  disabled={!saveLabel.trim()}
                  className="px-2 py-1 text-xs font-medium text-white bg-blue-500 rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsSaving(false);
                    setSaveLabel('');
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsSaving(true)}
                className="flex items-center gap-2 w-full px-2 py-1.5 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Save current color
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
